import { CognitoAuth } from 'amazon-cognito-auth-js/dist/amazon-cognito-auth'
import { CognitoUserPool } from 'amazon-cognito-identity-js'
import { config as AWSConfig } from 'aws-sdk'
import { cognitoConfig } from '../../config/cognito.js'
import { clearSession, setSessionCookie } from './session';
import { SET_SESSION } from './../../actions/types';
import apiConfig from '../../config/api.js';
import {loadUser} from './../../actions/user';
import store from './../../store/index'
import TokenStorage from './../utils/token_storage';
import { startDemoSession, isDemoSession, storedSession } from './demo';
const axios = require('axios');


// Initialise the Cognito sesson from a callback href
export function initSessionFromCallbackURI (callbackHref) {
  return function (dispatch) {
    return parseCognitoWebResponse(callbackHref) // parse the callback URL
      .then(() => getCognitoSession(dispatch)) // get a new session
      .then((session) => {
        setSessionCookie(session);
        dispatch({ type: SET_SESSION, session })
      })
  }
}

AWSConfig.region = cognitoConfig.region

// Creates a CognitoAuth instance
const createCognitoAuth = () => {
  const appWebDomain = cognitoConfig.userPoolBaseUri.replace('https://', '').replace('http://', '')
  const auth = new CognitoAuth({
    UserPoolId: cognitoConfig.userPool,
    ClientId: cognitoConfig.clientId,
    AppWebDomain: appWebDomain,
    TokenScopesArray: cognitoConfig.tokenScopes,
    RedirectUriSignIn: cognitoConfig.callbackUri,
    RedirectUriSignOut: cognitoConfig.signoutUri
  })
  return auth
}

// Creates a CognitoUser instance
const createCognitoUser = () => {
  const pool = createCognitoUserPool()
  return pool.getCurrentUser()
}

// Creates a CognitoUserPool instance
const createCognitoUserPool = () => new CognitoUserPool({
  UserPoolId: cognitoConfig.userPool,
  ClientId: cognitoConfig.clientId
})

// Get the URI of the hosted sign in screen
const getCognitoSignInUri = () => {
  const signinUri = `${cognitoConfig.userPoolBaseUri}/login?response_type=code&client_id=${cognitoConfig.clientId}&redirect_uri=${cognitoConfig.callbackUri}`
  return signinUri
}

// Parse the response from a Cognito callback URI (assumed a token or code is in the supplied href). Returns a promise.
const parseCognitoWebResponse = (href) => {
  return new Promise((resolve, reject) => {
    const auth = createCognitoAuth()

    // userHandler will trigger the promise
    auth.userhandler = {
      onSuccess: function (result) {
        resolve(result)
      },
      onFailure: function (err) {
        reject(new Error('Failure parsing Cognito web response: ' + err))
      }
    }
    auth.parseCognitoWebResponse(href)
  })
}

// Gets a new Cognito session. Returns a promise.
const getCognitoSession = (dispatch) => {
  return new Promise((resolve, reject) => {
    const cognitoUser = createCognitoUser()
    cognitoUser.getSession((err, result) => {
      if (err || !result) {
        reject(new Error('Failure getting Cognito session: ' + err))
        return
      }
      // given_name/family_name are optional Cognito attributes. String
      // concatenation turned a missing one into the literal text "undefined",
      // which is what got written to the users row and rendered on the
      // dashboard. Omit absent attributes instead, and encode the rest.
      const claims = result.idToken.payload;
      const params = new URLSearchParams({ username: claims['cognito:username'] });
      if (claims.email) params.set('email', claims.email);
      if (claims.given_name) params.set('first_name', claims.given_name);
      if (claims.family_name) params.set('last_name', claims.family_name);
      /*
       * firstOrNew verifies the token and matches it against the username in
       * the query string, so this call has to carry the header. It always
       * could: getSession has already resolved result.accessToken by here, and
       * formatSessionObject below reads the same jwtToken.
       */
      axios.get( apiConfig.host + apiConfig.port + `/api/user/firstOrNew?` + params.toString(),
        { headers: { Authorization: `Bearer ${result.accessToken.jwtToken}` } }
        ).then(data => {
        const session = formatSessionObject(data.data.id, result);
        TokenStorage.setToken({access_token : session.jwt, refresh_token : null});
        session.user = {...session.user, ...data.data[0]};
        // loadUser goes through getOne(), which builds /api/user/:id and never
        // looks at wheres, so the email filter here was requesting
        // /api/user/null and writing null over the store's user record.
        dispatch(loadUser(session, {id : data.data.id}));
        resolve(session);
      });     
    })
  })
}


const formatSessionObject = (id, result) =>
{
  const session = {
    user: {
      id : id,
      userName: result.idToken.payload['cognito:username'],
      email: result.idToken.payload.email
    },
    jwt : result.accessToken.jwtToken,
    groups : result.idToken.payload['cognito:groups'],
    /*
     * The group is 'admins' - the name aws_cognito_user_group.admins creates
     * and the one middleware/Viewer.js reads through ADMIN_GROUP. This read
     * 'Admin' and so was false for every account that has ever existed.
     *
     * Stamped at token issue, maxAge 3600: being added to the group does
     * nothing for the token already in the browser. Sign out and back in.
     */
    isAdmin : Array.isArray(result.idToken.payload['cognito:groups']) && result.idToken.payload['cognito:groups'].indexOf('admins') !== -1,
    expiration : result.accessToken.payload.exp,
    isLoggedIn : true
  }
  session.user = {...session.user};
  return session;
}

export const refresh = (id = null) =>
{
  /*
   * A demo session has no Cognito user to getSession() from. This is called
   * from hasSession() five minutes before expiry and from the api middleware
   * on any 401, so without this branch a demo dies an hour in by redirecting
   * to the hosted UI - which reads as the demo being broken rather than as the
   * token having aged out.
   */
  if (isDemoSession()) {
    const previous = storedSession();
    return startDemoSession(previous.demoKey).then(session => {
      TokenStorage.setToken({access_token : session.jwt, refresh_token : null});
      setSessionCookie(session);
      store.dispatch({ type: SET_SESSION, session });
      return session;
    });
  }
  return new Promise((resolve, reject) => {
    const auth = createCognitoAuth();
    auth.userhandler = {
      onSuccess: function (result) {
        let session = formatSessionObject(store.getState().session.user.id, result);
        TokenStorage.setToken({access_token : session.jwt, refresh_token : null});
        setSessionCookie(session);
        store.dispatch({ type: SET_SESSION, session });
        resolve(session)
      },
      onFailure: function (err) {
        reject(err)
      }
    }
    auth.getSession();
  });    
}

// Sign out of the current session (will redirect to signout URI)
const signOutCognitoSession = () => {
  const wasDemo = isDemoSession();
  clearSession();
  TokenStorage.clearToken();
  if (wasDemo) {
    // auth.signOut() redirects to the Cognito hosted signout, which has no
    // record of this session and bounces to an error page rather than home.
    window.location.href = '/';
    return;
  }
  const auth = createCognitoAuth()
  auth.signOut()
}

export default {
  createCognitoAuth,
  createCognitoUser,
  createCognitoUserPool,
  getCognitoSession,
  getCognitoSignInUri,
  parseCognitoWebResponse,
  signOutCognitoSession,
  initSessionFromCallbackURI
}